import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, query } from "../../lib/api";
import { formatAmount, formatRelative } from "../../lib/format";
import type { Customer, ListResponse } from "../../lib/types";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  Pagination,
  Sheet,
  SheetHeader,
  Skeleton,
  Table,
  Td,
  TextAreaField,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

/**
 * Customers is the merchant's own address book.
 *
 * Most rows here are never typed in by hand: a payer who leaves an email or a number on
 * a payment link becomes a customer automatically, so this page fills itself as the
 * merchant trades. Adding one manually is for the cases that come first — the client a
 * merchant is about to invoice.
 */
export function Customers() {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Customer | "new" | null>(null);

  const customers = useQuery({
    queryKey: ["customers", offset, search],
    queryFn: () =>
      api.get<ListResponse<Customer>>(
        `/api/dashboard/customers${query({ limit: PAGE_SIZE, offset, search })}`,
      ),
  });

  const rows = customers.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Customers"
        description="The people and firms who pay you. Payers who give a contact detail are added here on their own."
        actions={<Button variant="primary" onClick={() => setEditing("new")}>Add customer</Button>}
      />

      <Sheet className="settle settle-1 overflow-hidden">
        <SheetHeader
          title="All customers"
          actions={
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              placeholder="Search name, email or number"
              aria-label="Search customers"
              className="w-56 rounded-md border border-rule-strong bg-paper-raised px-3 py-1.5 text-[13px] placeholder:text-ink-faint focus:border-verdigris-bright focus:outline-none focus:ring-2 focus:ring-verdigris-bright/15"
            />
          }
        />

        {customers.isLoading ? (
          <Skeleton rows={6} />
        ) : customers.error ? (
          <ErrorNotice error={customers.error} className="m-5" />
        ) : rows.length === 0 ? (
          <EmptyState
            title={search ? "No customers match that search" : "No customers yet"}
            description={
              search
                ? "Try a different name, email address or number."
                : "Add one now, or let your first payment link fill this in for you."
            }
            action={
              !search && (
                <Button variant="primary" onClick={() => setEditing("new")}>
                  Add customer
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Contact</Th>
                  <Th align="right">Payments</Th>
                  <Th align="right">Paid</Th>
                  <Th>Last paid</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((customer) => (
                  <Tr key={customer.id}>
                    <Td>
                      <span className="font-medium text-ink-strong">{customer.display_name}</span>
                      {customer.reference && (
                        <span className="ml-2 font-mono text-[11.5px] text-ink-faint">
                          {customer.reference}
                        </span>
                      )}
                    </Td>
                    <Td className="text-ink-muted">
                      {customer.mobile ?? customer.email ?? "—"}
                    </Td>
                    <Td align="right" className="tabular">
                      {customer.payment_count ?? 0}
                    </Td>
                    <Td align="right" className="tabular">
                      {customer.total_paid
                        ? `${customer.total_paid.currency} ${formatAmount(customer.total_paid.minor)}`
                        : "—"}
                    </Td>
                    <Td className="text-ink-muted">{formatRelative(customer.last_payment_at)}</Td>
                    <Td align="right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(customer)}>
                        Edit
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={customers.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>

      <CustomerDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          queryClient.invalidateQueries({ queryKey: ["customers"] });
        }}
      />
    </>
  );
}

function CustomerDialog({
  editing,
  onClose,
  onSaved,
}: {
  editing: Customer | "new" | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = editing && editing !== "new" ? editing : null;
  const [form, setForm] = useState({ name: "", email: "", mobile: "", reference: "", notes: "" });
  const [seeded, setSeeded] = useState<string | null>(null);

  // Seeding on render rather than in an effect keeps the dialog's fields in step with
  // whichever row was opened, without a flash of the previous customer's details.
  const key = existing?.id ?? "new";
  if (editing && seeded !== key) {
    setSeeded(key);
    setForm({
      name: existing?.name ?? "",
      email: existing?.email ?? "",
      mobile: existing?.mobile ?? "",
      reference: existing?.reference ?? "",
      notes: existing?.notes ?? "",
    });
  }

  const save = useMutation({
    mutationFn: () =>
      existing
        ? api.patch<Customer>(`/api/dashboard/customers/${existing.id}`, form)
        : api.post<Customer>("/api/dashboard/customers", form),
    onSuccess: onSaved,
  });

  // Archived, not deleted: this customer's payments and invoices still point at them,
  // and a merchant's history should not develop holes because someone tidied a list.
  const archive = useMutation({
    mutationFn: () =>
      api.del<Customer>(
        `/api/dashboard/customers/${existing?.id}${existing?.archived ? "?restore=true" : ""}`,
      ),
    onSuccess: onSaved,
  });

  return (
    <Modal
      open={editing !== null}
      onClose={onClose}
      title={existing ? "Edit customer" : "Add customer"}
      description="A customer needs an email address or a mobile number, so you can reach them and so their payments can be matched to them."
      footer={
        <>
          {existing && (
            <Button
              variant={existing.archived ? "secondary" : "danger"}
              loading={archive.isPending}
              onClick={() => archive.mutate()}
              className="mr-auto"
            >
              {existing.archived ? "Restore" : "Archive"}
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {existing ? "Save changes" : "Add customer"}
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ErrorNotice error={save.error ?? archive.error} />
        <Field
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Rusape Mining"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Email address"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="accounts@example.co.zw"
          />
          <Field
            label="Mobile number"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            placeholder="0771 234 567"
          />
        </div>
        <Field
          label="Your reference"
          hint="Optional — the identifier this customer has in your own system."
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
        />
        <TextAreaField
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
